import React, { useContext, useEffect, useMemo, useState, useRef } from 'react'
import styled from 'styled-components'
import { Eye, Pencil, Trash2, Download } from 'lucide-react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { CertificateOutletType } from '..'
import axios from '../../../../config/axios'
import ApiService from '../../../../services/ApiService'
import {
  downloadCertificatePdfFromMappedData,
  mapCertificateApiToPdfData,
} from '../../../../components/Certificates/utils/mapCertificateApiToPdfData'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import ConfirmationModal from '../../../../components/ModalConfirmation'
import { StandardDataTable, useColumnsState } from '../../../../components/StandardPage'
import { buildCertificatesColumns, certificatesScreenKey, CertificateRow } from './certificatesColumns'

const TableRoot = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
`

const CertificatesTable: React.FC = () => {
  const [certificates, setCertificates] = useState<CertificateRow[]>([])
  const [visibleCount, setVisibleCount] = useState<number>(50)
  const [totalCount, setTotalCount] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const { appliedFilters, search, refresh, setLoadingHeader } = useOutletContext<CertificateOutletType>()
  const navigate = useNavigate()
  const { setFeedback } = useContext(FeedbackContext)

  const [deleteModal, setDeleteModal] = useState<{isOpen: boolean, id: number | null}>({ isOpen: false, id: null })

  const columns = useMemo(() => buildCertificatesColumns(), [])
  const { columnsState, handleColumnsChange, handleRestoreColumns } = useColumnsState(certificatesScreenKey, columns)

  const handleDownloadCertificate = async (certificateId: number) => {
    try {
      const [resFind, resUtils] = await Promise.all([
        ApiService.certificates.findById(certificateId),
        ApiService.certificates.getUtils(certificateId),
      ])
      const pdfData = mapCertificateApiToPdfData(
        resFind.data,
        resUtils.data,
        certificateId,
      )
      await downloadCertificatePdfFromMappedData(pdfData)
    } catch {
      setFeedback({
        isOpen: true,
        message: 'Não foi possível gerar o PDF do certificado. Tente novamente.',
        statusOk: false,
      })
    }
  }

  const handleConfirmDelete = async () => {
    if (deleteModal.id !== null) {
      await deleteCertificate(deleteModal.id)
      setDeleteModal({ isOpen: false, id: null })
    }
  }

  const deleteCertificate = async (id: number) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/certificates/certificate/${id}`,
      )
      setCertificates((prev) =>
        prev.filter((cert) => cert.id !== id),
      )
      if (totalCount !== null) {
        setTotalCount((prev) => (prev ? prev - 1 : prev))
      }
      setFeedback({
        isOpen: true,
        message: 'Certificado deletado com sucesso!',
        statusOk: true,
      })
    } catch {
      setFeedback({
        isOpen: true,
        message: 'Erro ao deletar certificado',
        statusOk: false,
      })
    }
  }

  const isFirstRender = useRef(true)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    if (refresh) fetchCertificates()
  }, [refresh])

  useEffect(() => {
    fetchCertificates()
  }, [appliedFilters, search, visibleCount])

  const fetchCertificates = () => {
    setIsLoading(true)
    setLoadingHeader?.(true)
    const params: Record<string, string | number> = {
      limit: visibleCount,
      offset: 0,
    }
    if (search?.trim()) {
      params.search = search.trim()
    }
    if (appliedFilters.length > 0) {
      params.filters = JSON.stringify(appliedFilters)
    }

    axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/certificates/certificate`, { params })
      .then(response => {
        if (response.data) {
          if (Array.isArray(response.data)) {
            setCertificates(response.data)
            setTotalCount(response.data.length)
          } else if (response.data.data) {
            setCertificates(response.data.data)
            setTotalCount(response.data.total ?? response.data.data.length)
          }
        }
      })
      .catch(error => {
        console.error(error)
      })
      .finally(() => {
        setIsLoading(false)
        setLoadingHeader?.(false)
      })
  }

  return (
    <TableRoot>
      <StandardDataTable
        columns={columns}
        data={certificates}
        columnsState={columnsState}
        onColumnsChange={handleColumnsChange}
        onRestoreColumns={handleRestoreColumns}
        totalCount={totalCount}
        onLoadMore={() => setVisibleCount(prev => prev + 50)}
        hasMore={typeof totalCount === 'number' ? certificates.length < totalCount : false}
        isLoading={isLoading}
        countLabel='certificados'
        tableName='Certificate'
        exportFileName='certificados'
        emptyMessage='Nenhum certificado encontrado.'
        rowActions={(certificateRow) => [
          { label: 'Visualizar', icon: <Eye size={16} />, onClick: () => navigate(`view/${certificateRow.id}`) },
          { label: 'Editar', icon: <Pencil size={16} />, onClick: () => navigate(`edit?id=${certificateRow.id}`) },
          { label: 'Baixar Certificado', icon: <Download size={16} />, onClick: () => handleDownloadCertificate(certificateRow.id) },
          { label: 'Excluir', icon: <Trash2 size={16} />, onClick: () => setDeleteModal({ isOpen: true, id: certificateRow.id }) },
        ]}
      />

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        title="Tem certeza que deseja excluir?"
        message="Esta ação não poderá ser desfeita. O certificado será removido e os estoques relacionados serão desvinculados."
        actions={[
          {
            label: 'Cancelar',
            onClick: () => setDeleteModal({ isOpen: false, id: null }),
            variant: 'outline'
          },
          {
            label: 'Excluir',
            onClick: handleConfirmDelete,
            variant: 'danger'
          }
        ]}
      />
    </TableRoot>
  )
}
export default CertificatesTable
